/**
 * useUploadWorker.ts
 * @version 3.1.0
 *
 * Fusion Phase Sync :
 *   - PDF generation + 3 emails CONSERVÉS (aucune régression)
 *   - MAX_RETRIES → markQueueItemFailed() au lieu de removeFromQueue()
 *   - Worker skip les items status:'failed' (pas de boucle infinie)
 *   - Retry manuel disponible via retryQueueItem() depuis l'UI horloge
 *   - clearDraft via offlinePhotoStore (plus d'import dynamique)
 *
 * v3.1.0 — Fix critique AbortController signal propagation
 *   - Le signal abort est maintenant passé au fetch() via processItem(signal)
 *   - Avant : timeout côté client → fetch continuait côté backend → BadRequestError: request aborted
 *   - Après : timeout → fetch annulé proprement → backend libéré immédiatement
 */

/// <reference types="vite/client" />

import { useEffect, useRef, useCallback }              from 'react'
import { useAppStore }                                 from '../store'
import { photoMemory }                                 from '../store/photoMemoryStore'
import { offlinePhotoStore, InspectionType, PhotoMap } from '../store/offlinePhotoStore'
import { apiService }                                  from './myrentcarApi'
import { generatePDF }                                 from './pdfService'
import { buildAuthHeader }                             from '../utils/auth'
import { BACKEND }                                     from '../utils/config'
import { tryMakeInspectionKey }                        from '../utils/inspectionKey'
import toast                                           from 'react-hot-toast'
import { useSyncStatus }                               from '../store/useSyncStatus'
import { usePendingMrcStore }                          from '../store/usePendingMrcStore'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface QueueItem {
  id:         string
  contractId: string
  type:       InspectionType
  retries:    number
  /** ✅ v3.0 — status géré par markQueueItemFailed */
  status?:    'pending' | 'failed' | 'uploaded'
  inspection: {
    mileage:              number | null
    fuel:                 number | null
    damages:              unknown[]
    completedAt:          string
    startedAt?:           string | null
    deviceInfo:           unknown | null
    signatureAgent?:      string | null
    signatureClient?:     string | null
    securityCheck?:       unknown | null
    accidentDeclaration?: string | null
    photosChecked?:       boolean
    contract: {
      id:              string
      rawId?:          string
      plate:           string
      clientName:      string
      clientEmail?:    string
      agencyEmail?:    string
      agenceDepart?:   string | null
      numeroDocument?: string | null
    }
  }
}

interface UploadResult {
  mrcPdfId?:    string
  mrcPhotoIds?: Record<string, string>
  stats?: {
    mrcErrors?:    number
    mrcPhotos?:    number
    supabasePhotos?: number
    pdfUploaded?:  boolean
  }
}

export class UploadError extends Error {
  constructor(
    message:                     string,
    public readonly retryable:   boolean = true,
    public readonly statusCode?: number,
  ) {
    super(message)
    this.name = 'UploadError'
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const MAX_RETRIES           = 8
const POLL_INTERVAL         = 10_000
const ONLINE_DEBOUNCE       = 2_000
const ITEM_TIMEOUT_MS       = 120_000
// Backoff exponentiel : 5s, 15s, 45s, 90s, 120s, 120s, 120s, 120s
// Donne ~8.5 min au réseau mobile pour se stabiliser (vs 25s avant)
const BACKOFF_BASE_MS       = 5_000
const BACKOFF_MAX_MS        = 120_000
// ✅ FIX Bug #2 — Timeout de sécurité pour éviter que _globalProcessing reste
// bloqué à true indéfiniment si le finally ne s'exécute jamais (crash, unmount brutal)
const LOCK_SAFETY_TIMEOUT_MS = 5 * 60 * 1000  // 5 minutes max par cycle (augmenté pour backoff)

// ─────────────────────────────────────────────────────────────────────────────
// Verrou global + ref partagée
// ─────────────────────────────────────────────────────────────────────────────

export let _globalProcessing = false
let _lockSafetyTimer: ReturnType<typeof setTimeout> | null = null

/** ✅ FIX Bug #2 — Reset du verrou. Appelé en cas de timeout de sécurité. */
function acquireLock(): boolean {
  if (_globalProcessing) return false
  _globalProcessing = true
  // Reset automatique si le finally ne s'exécute jamais
  _lockSafetyTimer = setTimeout(() => {
    if (_globalProcessing) {
      console.warn('[UploadWorker] ⚠️ Verrou bloqué détecté — reset forcé après 3 min')
      _globalProcessing = false
      useSyncStatus.getState().setSyncing(false)
      useSyncStatus.getState().setCurrentItem(null)
    }
  }, LOCK_SAFETY_TIMEOUT_MS)
  return true
}

function releaseLock(): void {
  _globalProcessing = false
  if (_lockSafetyTimer) {
    clearTimeout(_lockSafetyTimer)
    _lockSafetyTimer = null
  }
}

export const uploadWorkerRef: {
  current: ((contractId?: string) => Promise<void>) | null
} = { current: null }

// ─────────────────────────────────────────────────────────────────────────────
// Helpers purs
// ─────────────────────────────────────────────────────────────────────────────



function buildDualPhotos(
  photos: PhotoMap
): Record<string, { dataUrl: string; originalDataUrl: string }> {
  return Object.fromEntries(
    Object.entries(photos).flatMap(([zone, p]) => {
      const dataUrl         = p?.dataUrl         ?? null
      const originalDataUrl = p?.originalDataUrl ?? dataUrl
      return dataUrl ? [[zone, { dataUrl, originalDataUrl }]] : []
    })
  )
}

/**
 * Calcule le délai de backoff exponentiel pour le retry N.
 * 0 → 5s, 1 → 15s, 2 → 45s, 3 → 90s, 4+ → 120s
 */
function getBackoffDelay(retryCount: number): number {
  const delay = BACKOFF_BASE_MS * Math.pow(3, retryCount)
  return Math.min(delay, BACKOFF_MAX_MS)
}

/**
 * Vérifie la connectivité réelle vers le backend.
 * navigator.onLine est non fiable sur tablettes Android (WiFi instable, changement de borne).
 * On fait un HEAD /healthz qui est ultra-léger (pas de body, pas de parsing JSON).
 * Timeout court (5s) : si ça ne répond pas en 5s, on considère offline.
 */
async function isReallyOnline(): Promise<boolean> {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 5_000)
    const res = await fetch(`${BACKEND}/healthz`, {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-store',
    })
    clearTimeout(timer)
    return res.ok
  } catch {
    return false
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useUploadWorker() {
  const intervalRef    = useRef<ReturnType<typeof setInterval> | null>(null)
  const onlineTimerRef = useRef<ReturnType<typeof setTimeout>  | null>(null)

  const credentials        = useAppStore(s => s.credentials)
  const uploadQueue        = useAppStore(s => s.uploadQueue)
  const removeFromQueue    = useAppStore(s => s.removeFromUploadQueue)
  const incrementRetry     = useAppStore(s => s.incrementQueueRetry)
  // ✅ v3.0 — nouvelles actions depuis useAppStore.js v2.1
  const markQueueItemFailed = useAppStore(s => s.markQueueItemFailed)

  // ── Traitement d'un item ──────────────────────────────────────────────────

  const processItem = useCallback(async (item: QueueItem, signal?: AbortSignal): Promise<void> => {
    if (!credentials) throw new UploadError('Credentials manquants', false)

    const { contractId, type, inspection } = item
    const { contract } = inspection

    // ✅ v5.0 — Construire l'inspectionKey pour le nettoyage
    const inspectionKey = tryMakeInspectionKey(type, contract) ?? contractId

    console.log(
      `[UploadWorker] ▶ Item ${item.id} — ${inspectionKey}` +
      ` (tentative ${item.retries + 1}/${MAX_RETRIES})`
    )

    // ── Photos : mémoire → IndexedDB fallback ─────────────────────────────
    let photos: PhotoMap = (typeof (photoMemory as any).getPhotos === 'function'
      ? await (photoMemory as any).getPhotos()
      : {}) as PhotoMap

    if (!Object.keys(photos).length) {
      console.log('[UploadWorker] photoMemory vide → fallback IndexedDB')
      // ✅ v5.0 — utiliser inspectionKey pour récupérer les photos
      photos = await offlinePhotoStore.getPhotos(inspectionKey, type)
    }

    // ── Génération PDF (CONSERVÉ) ─────────────────────────────────────────
    const fullInspection = { ...inspection, photos }
    const pdfResult      = await generatePDF(fullInspection)

    if (!pdfResult?.pdfBase64) {
      throw new UploadError('Génération PDF échouée', true)
    }

    const { pdfBase64 } = pdfResult
    const pdfFilename   = `edl-${type}-${contract.plate}-${new Date().toISOString().slice(0, 10)}.pdf`

    const startObj = new Date(inspection.startedAt || Date.now())
    const endObj   = new Date(inspection.completedAt || Date.now())
    const dureeSec = Math.max(0, Math.floor((endObj.getTime() - startObj.getTime()) / 1000))

    // ── Upload backend ────────────────────────────────────────────────────
    // ✅ v3.1.0 — Passer le signal AbortController pour annuler proprement le fetch
    // si le timeout de 120s est atteint côté client. Sans ce signal, le backend
    // continue à recevoir des données après la déconnexion → BadRequestError: request aborted
    const res = await fetch(`${BACKEND}/api/edl/save`, {
      method:  'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-MRC-Auth':   buildAuthHeader(credentials as any),
      },
      body: JSON.stringify({
        contractId,
        type,
        mileage:     inspection.mileage,
        fuel:        inspection.fuel,
        damages:     inspection.damages ?? [],
        photos:      buildDualPhotos(photos),
        completedAt: inspection.completedAt,
        agentName:   credentials.username,
        deviceInfo:  inspection.deviceInfo ?? null,
        pdfBase64,
        pdfFilename,
        duree:       dureeSec,
        immat:       contract.plate,
        clientName:  contract.clientName,
        agence:      contract.agenceDepart || null,
        numeroDocument: contract.numeroDocument || null,
        inspectionKey,
      }),
    })

    if (!res.ok) {
      // ✅ FIX v2 — Distinguer un vrai 400 (payload invalide) d'un abort réseau
      // Express/raw-body renvoie 400 quand la connexion coupe pendant la réception du body.
      // Le backend renvoie maintenant 503 dans ce cas, mais par sécurité on vérifie aussi
      // le body de la réponse pour les cas edge où le middleware n'intervient pas.
      let retryable: boolean
      if (res.status === 503 || res.status === 502 || res.status === 504 || res.status === 429) {
        // Erreurs serveur / rate-limit → toujours retryable
        retryable = true
      } else if (res.status === 401 || res.status === 403 || res.status === 422) {
        // Erreurs auth / validation → jamais retryable
        retryable = false
      } else if (res.status === 400) {
        // HTTP 400 ambigu — lire le body pour trancher
        try {
          const errBody = await res.json()
          const errMsg = String(errBody?.error || '').toLowerCase()
          // Si c'est un abort réseau, retryable. Sinon (champ manquant, etc.), fatal.
          retryable = errMsg.includes('abort') || errMsg.includes('connexion') || errMsg.includes('interrompue')
        } catch {
          retryable = true // impossible de lire le body → probablement réseau coupé
        }
      } else if (res.status >= 500) {
        retryable = true
      } else {
        retryable = false
      }
      throw new UploadError(`HTTP ${res.status}`, retryable, res.status)
    }

    const saveData: UploadResult = await res.json()
    const photoCount  = Object.keys(saveData.mrcPhotoIds ?? {}).length
    const photoSent   = Object.keys(buildDualPhotos(photos)).length
    // ✅ FIX Bug #1 — Utiliser mrcErrors du backend si disponible (plus précis)
    const mrcErrors   = saveData.stats?.mrcErrors ?? (photoSent > 0 && photoCount === 0 ? photoSent : photoSent - photoCount)
    console.log(`[UploadWorker] EDL sauvegardé ✅ — ${photoCount}/${photoSent} photos MRC`)

    // ✅ FIX Bug #1 — Avertissement si des photos n'ont pas été uploadées dans MRC
    // Promise.allSettled masque les erreurs MRC → l'upload paraît réussi mais les photos manquent
    if (photoSent > 0 && photoCount === 0) {
      console.warn(`[UploadWorker] ⚠️ ${photoSent} photos envoyées mais 0 enregistrées dans MRC (session expirée ?)`);
      toast(
        `EDL synchronisé mais les photos n'ont pas pu être enregistrées dans MRC.\nVérifiez les logs ou reconnectez-vous.`,
        { icon: '⚠️', duration: 10_000 }
      )
    } else if (mrcErrors > 0) {
      console.warn(`[UploadWorker] ⚠️ ${mrcErrors} photo(s) non uploadée(s) dans MRC`);
    }

    // ── Envoi des 3 emails (client + agence + Olympic) ──────────────────────
    const typeLabel    = type === 'depart' ? 'de départ' : 'de retour'
    const emailTasks: Promise<void>[] = []

    // 1. Email client
    if (contract.clientEmail) {
      emailTasks.push(
        apiService.sendEmail(
          contract.clientEmail, contract.clientName,
          `Votre état des lieux ${typeLabel} — ${contract.plate}`,
          pdfBase64, pdfFilename, contractId, credentials, contract.plate,
        ).catch(e => console.warn('[UploadWorker] Email client:', (e as Error).message))
      )
    }

    // 2. Email agence
    if (contract.agencyEmail) {
      emailTasks.push(
        apiService.sendEmail(
          contract.agencyEmail, 'Agence Olympic',
          `EDL ${type === 'depart' ? 'Départ' : 'Retour'} — ${contract.plate} — ${contract.clientName}`,
          pdfBase64, pdfFilename, contractId, credentials, contract.plate,
        ).catch(e => console.warn('[UploadWorker] Email agence:', (e as Error).message))
      )
    }

    // 3. Email Olympic superviseur (migré depuis DoneScreen v3.0)
    const OLYMPIC_DEFAULT_EMAIL = 'etatsolympiclocation@gmail.com'
    emailTasks.push(
      apiService.sendEmail(
        OLYMPIC_DEFAULT_EMAIL, 'Olympic Location',
        `EDL ${type === 'depart' ? 'Départ' : 'Retour'} — ${contract.plate} — ${contract.clientName}`,
        pdfBase64, pdfFilename, contractId, credentials, contract.plate,
      ).catch(e => console.warn('[UploadWorker] Email Olympic:', (e as Error).message))
    )

    await Promise.allSettled(emailTasks)

    // ── Nettoyage (CONSERVÉ + clearDraft migré) ───────────────────────────
    // ✅ v5.0 — utiliser inspectionKey pour le nettoyage
    await offlinePhotoStore.clearPhotos(inspectionKey, type).catch(() => {})
    await offlinePhotoStore.clearDraft(inspectionKey, type).catch(() => {})
    // ✅ Phase 1 — Libérer le cache des photos de départ après un retour réussi
    if (type === 'retour') {
      await offlinePhotoStore.clearDepartureCache(contractId).catch(() => {})
    }

    // ✅ Retirer de la queue — UNIQUEMENT en cas de succès
    removeFromQueue(contractId)
    toast.success(`EDL ${type} synchronisé ✓`, { duration: 4_000 })

    // ── Alerte MRC : confirmer sur le backend (retour uniquement) ──────────
    // ✅ FIX — Toujours tenter l'alerte, même si pendingItem absent du store local
    // (l'agent peut avoir rechargé l'app ou utiliser une autre tablette)
    if (type === 'retour') {
      try {
        const storeItem = usePendingMrcStore.getState().pendingReturns.find(r => r.id === contractId);
        // Construire l'agencyCode avec fallback sur le contrat directement
        const agencyCode = storeItem?.agencyCode
          || String((contract as any).agenceDepartCode || '')

        const alertPayload = {
          id:          storeItem?.id ?? contractId,
          plate:       storeItem?.plate       ?? contract.plate        ?? 'Inconnu',
          vehicleName: storeItem?.vehicleName  ?? (contract as any).vehicleName ?? 'Véhicule',
          clientName:  storeItem?.clientName   ?? contract.clientName  ?? 'Client',
          agencyCode,
          completedAt: storeItem?.completedAt  ?? inspection.completedAt,
          agentName:   storeItem?.agentName    ?? credentials.username ?? 'Agent',
          kmDepart:    storeItem?.kmDepart     ?? null,
          kmRetour:    storeItem?.kmRetour     ?? inspection.mileage   ?? null,
          fuelDepart:  storeItem?.fuelDepart   ?? null,
          fuelRetour:  storeItem?.fuelRetour   ?? inspection.fuel      ?? null,
        }

        if (!agencyCode) {
          console.warn(`[UploadWorker] ⚠️ agencyCode vide pour ${contractId} — alerte MRC non envoyée (non bloquant)`);
        } else {
          const alertRes = await fetch(`${BACKEND}/api/mrc-alerts`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-MRC-Auth': buildAuthHeader(credentials as any),
            },
            body: JSON.stringify(alertPayload),
          });
          if (alertRes.ok) {
            usePendingMrcStore.getState().confirmPendingReturn(contractId);
            console.log(`[UploadWorker] ✅ Alerte MRC confirmée → ${contractId}`);
          } else {
            console.warn(`[UploadWorker] ⚠️ Alerte MRC refusée par le backend: ${alertRes.status}`);
          }
        }
      } catch (e) {
        console.warn('[UploadWorker] Alerte MRC non envoyée (non bloquant):', (e as Error).message);
      }
    }

  }, [credentials, removeFromQueue])

  // ── Timeout par item ──────────────────────────────────────────────────────

  const processItemWithTimeout = useCallback(async (item: QueueItem): Promise<void> => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), ITEM_TIMEOUT_MS)
    try {
      // ✅ v3.1.0 — Passer controller.signal à processItem → propagé au fetch
      // Avant : le fetch ignorait l'abort → backend recevait une connexion fermée brutalement
      // Après : fetch annulé proprement avec signal → serveur libéré, pas de BadRequestError
      await processItem(item, controller.signal)
    } catch (err) {
      // Si l'abort a déclenché un DOMException 'AbortError', on le convertit en UploadError retryable
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new UploadError(`Timeout ${ITEM_TIMEOUT_MS / 1000}s dépassé — upload annulé proprement`, true)
      }
      throw err
    } finally {
      clearTimeout(timer)
    }
  }, [processItem])

  // ── Boucle principale ─────────────────────────────────────────────────────

  const processQueue = useCallback(async (contractId?: string): Promise<void> => {
    // ✅ FIX Bug #2 — Utiliser acquireLock() au lieu de la comparaison directe
    // acquireLock() pose aussi un timeout de sécurité automatique (5 min)
    if (!acquireLock())       return
    if (!credentials)         { releaseLock(); return }

    // ✅ FIX v2 — Remplacer navigator.onLine par une probe réseau réelle
    // navigator.onLine est non fiable sur tablettes Android WiFi :
    //   - Renvoie false même avec une connexion fonctionnelle
    //   - Reste à true avec un réseau captif (portail WiFi)
    // La probe HEAD /healthz tranche en 5s maximum.
    const online = await isReallyOnline()
    if (!online) {
      console.log('[UploadWorker] Probe réseau ❌ — backend inaccessible, report du cycle')
      releaseLock()
      return
    }

    const queue = useAppStore.getState().uploadQueue as QueueItem[]
    const items = contractId
      ? queue.filter(i => i.contractId === contractId)
      : [...queue]

    // ✅ v3.0 — CORRECTION BUG A : skip les items 'failed'
    // Évite la boucle infinie sur erreurs fatales (payload corrompu, etc.)
    const itemsToProcess = items.filter(i => i.status !== 'failed' && i.status !== 'uploaded')

    if (!itemsToProcess.length) { releaseLock(); return }

    useSyncStatus.getState().setSyncing(true)
    try {
      for (const item of itemsToProcess) {
        try {
          useSyncStatus.getState().setCurrentItem(item.contractId)
          await processItemWithTimeout(item)
        } catch (err) {
          const isUploadError = err instanceof UploadError
          const retryable     = isUploadError ? err.retryable : true
          const message       = err instanceof Error ? err.message : String(err)

          console.warn(`[UploadWorker] ✗ Item ${item.id}:`, message)
          incrementRetry(item.id)

          const newRetries = item.retries + 1

          if (!retryable || newRetries >= MAX_RETRIES) {
            // ✅ v3.0 — CORRECTION BUG A
            // Avant : removeFromQueue() → données perdues définitivement
            // Après : markQueueItemFailed() → données conservées
            //         badge rouge dans l'UI horloge
            //         retry manuel via retryQueueItem()
            markQueueItemFailed(item.id)

            toast.error(
              `EDL ${item.type} — ${MAX_RETRIES} tentatives échouées.\n` +
              `Données conservées. Réessayez depuis la file d'attente.`,
              { duration: 8_000 }
            )

            console.error(
              `[UploadWorker] 🔴 MAX_RETRIES atteint — ${item.contractId}` +
              ` marqué 'failed'. Données préservées.`
            )
          } else {
            // ✅ FIX v2 — Backoff exponentiel : attendre avant le prochain item
            // Donne au réseau mobile le temps de se stabiliser
            const delay = getBackoffDelay(newRetries)
            console.log(`[UploadWorker] ⏳ Backoff ${Math.round(delay/1000)}s avant retry #${newRetries + 1}`)
            await new Promise(r => setTimeout(r, delay))
          }
        }
        // Pause minimale entre items (même si succès)
        await new Promise(r => setTimeout(r, 500))
      }
    } finally {
      // ✅ FIX Bug #2 — releaseLock() libère le verrou ET annule le timeout de sécurité
      releaseLock()
      useSyncStatus.getState().setSyncing(false)
      useSyncStatus.getState().setCurrentItem(null)
    }
  }, [credentials, processItemWithTimeout, incrementRetry, markQueueItemFailed])

  // ── Effets ────────────────────────────────────────────────────────────────

  useEffect(() => {
    uploadWorkerRef.current = processQueue
    return () => {
      uploadWorkerRef.current = null
      // ✅ FIX Bug #2 — Si le composant se démonte pendant un upload, libérer le verrou
      releaseLock()
    }
  }, [processQueue])

  useEffect(() => {
    const unsubQueue = useAppStore.subscribe(
      state => state.uploadQueue,
      (queue) => {
        const pending = (queue as QueueItem[]).filter(
          i => i.status !== 'failed' && i.status !== 'uploaded'
        )
        if (pending.length > 0) processQueue()
      }
    )

    const handleOnline = () => {
      if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current)
      onlineTimerRef.current = setTimeout(() => {
        console.log('[UploadWorker] Réseau rétabli (debounced)')
        // ✅ Fix #2 — Auto-reset des items 'failed' à 'pending' sur reconnexion
        // Sans ce reset, processQueue() voit la queue vide (tous 'failed') et ne fait rien.
        // L'utilisateur devait manuellement cliquer Sync → retryQueueItem → re-cliquer.
        const store = useAppStore.getState()
        ;(store.uploadQueue as QueueItem[]).forEach(item => {
          if (item.status === 'failed') store.retryQueueItem?.(item.id)
        })
        processQueue()
      }, ONLINE_DEBOUNCE)
    }

    window.addEventListener('online', handleOnline)

    intervalRef.current = setInterval(() => {
      // ✅ FIX v2 — Ne plus gate sur navigator.onLine ici.
      // La probe réseau réelle (isReallyOnline) est dans processQueue.
      // navigator.onLine est non fiable sur tablette → le poll était bloqué indéfiniment.
      const queue = useAppStore.getState().uploadQueue as QueueItem[]
      const hasPending = queue.some(
        i => i.status !== 'failed' && i.status !== 'uploaded'
      )
      if (hasPending && !_globalProcessing) processQueue()
    }, POLL_INTERVAL)

    processQueue()

    return () => {
      unsubQueue()
      window.removeEventListener('online', handleOnline)
      if (intervalRef.current)    clearInterval(intervalRef.current)
      if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current)
    }
  }, [processQueue])

  return {
    queueLength:  (uploadQueue as QueueItem[]).length,
    isProcessing: _globalProcessing,
    processQueue,
  } as const
}
